import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr, API, TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";
import {
  ArrowLeft, Download, Sparkles, Loader2, FileArchive, FileText, Activity,
  ListTree, LineChart as LineChartIcon, Files, Brain, Trash2,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { fmtBytes, SEV_COLOR, CHART_METRICS as CHARTS } from "@/lib/flightSessions";
import { UlogIntegrityCard } from "@/components/UlogIntegrityCard";

const TABS = [
  { id: "summary", label: "Résumé", icon: FileText },
  { id: "timeline", label: "Timeline", icon: Activity },
  { id: "charts", label: "Courbes", icon: LineChartIcon },
  { id: "events", label: "Événements", icon: ListTree },
  { id: "files", label: "Fichiers", icon: Files },
  { id: "ai", label: "Analyse IA", icon: Brain },
];

function StatCard({ label, value, testid }) {
  return (
    <div className="bg-[#111] border border-white/10 rounded-sm px-3 py-2" data-testid={testid}>
      <div className="font-mono text-[9px] tracking-[0.15em] uppercase text-[#525252]">{label}</div>
      <div className="font-mono text-sm mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

async function downloadAuthed(path, filename) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
}

function SeriesChart({ droneId, sessionId, metric, label, color }) {
  const [points, setPoints] = useState(null);
  const [empty, setEmpty] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(
          `/flight-sessions/${encodeURIComponent(droneId)}/${encodeURIComponent(sessionId)}/series`,
          { params: { metric } });
        if (!alive) return;
        const pts = (data.points || []).map((p) => ({ t: p.relative_time_s, v: p.value }));
        if (pts.length === 0) setEmpty(true);
        setPoints(pts);
      } catch {
        if (alive) setEmpty(true);
      }
    })();
    return () => { alive = false; };
  }, [droneId, sessionId, metric]);

  return (
    <div className="bg-[#111] border border-white/10 rounded-sm p-3" data-testid={`chart-${metric}`}>
      <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#A3A3A3] mb-2">{label}</div>
      <div style={{ height: 160, width: "100%" }}>
        {points === null && <div className="h-full flex items-center justify-center font-mono text-[10px] text-[#525252]">Chargement…</div>}
        {empty && <div className="h-full flex items-center justify-center font-mono text-[10px] text-[#525252]" data-testid={`chart-empty-${metric}`}>Données absentes</div>}
        {points && points.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#ffffff10" vertical={false} />
              <XAxis dataKey="t" tick={{ fill: "#525252", fontSize: 9 }} stroke="#ffffff20"
                tickFormatter={(v) => (v == null ? "" : `${v.toFixed(0)}s`)} />
              <YAxis tick={{ fill: "#525252", fontSize: 9 }} stroke="#ffffff20" width={36} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #ffffff22", fontSize: 10, fontFamily: "monospace" }} />
              <Line type="monotone" dataKey="v" stroke={color} dot={false} strokeWidth={1.5} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default function FlightSessionDetail() {
  const { drone_id, session_id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("summary");
  const [files, setFiles] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [ai, setAi] = useState({ quick: null, deep: null });
  const [aiBusy, setAiBusy] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [delOpen, setDelOpen] = useState(false);
  const [delBusy, setDelBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/flight-sessions/${encodeURIComponent(drone_id)}/${encodeURIComponent(session_id)}`);
      setDetail(data);
    } catch (e) {
      toast.error(apiErr(e, "Session introuvable"));
    }
  }, [drone_id, session_id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab === "files" && files === null) {
      api.get(`/flight-sessions/${encodeURIComponent(drone_id)}/${encodeURIComponent(session_id)}/files`)
        .then(({ data }) => setFiles(data.files || [])).catch(() => setFiles([]));
    }
    if ((tab === "timeline" || tab === "events") && timeline === null) {
      api.get(`/flight-sessions/${encodeURIComponent(drone_id)}/${encodeURIComponent(session_id)}/timeline`)
        .then(({ data }) => setTimeline(data.events || [])).catch(() => setTimeline([]));
    }
  }, [tab, files, timeline, drone_id, session_id]);

  const runAi = async (mode) => {
    setAiBusy(mode);
    try {
      const { data } = await api.post(
        `/flight-sessions/${encodeURIComponent(drone_id)}/${encodeURIComponent(session_id)}/analysis/${mode}`);
      setAi((a) => ({ ...a, [mode]: data }));
      toast.success(`Analyse ${mode}${data.cached ? " (cache)" : ""} prête`);
    } catch (e) {
      toast.error(apiErr(e, "Analyse IA indisponible"));
    } finally {
      setAiBusy("");
    }
  };

  const doDownload = async (path, filename) => {
    try {
      await downloadAuthed(path, filename);
    } catch (e) {
      toast.error("Téléchargement impossible");
    }
  };

  const s = detail?.summary;
  const ready = detail?.downloads || {};
  const base = `/flight-sessions/${encodeURIComponent(drone_id)}/${encodeURIComponent(session_id)}`;
  const fileSize = (p) => (files || []).find((f) => f.path === p)?.size;

  const doDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(base);
      toast.success("Session supprimée");
      navigate("/flight-sessions");
    } catch (e) {
      toast.error(apiErr(e, "Échec de la suppression"));
      setDelBusy(false);
      setDelOpen(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-[#0A0A0A] text-white" data-testid="flight-session-detail">
      <div className="max-w-6xl mx-auto px-6 py-6">
        <button onClick={() => navigate("/flight-sessions")} data-testid="fsd-back"
          className="flex items-center gap-1.5 font-mono text-[11px] uppercase text-[#A3A3A3] hover:text-white mb-4">
          <ArrowLeft size={14} /> Retour
        </button>

        <div className="flex items-start justify-between mb-5">
          <div>
            <h1 className="font-chivo font-black text-lg tracking-tight">{drone_id}</h1>
            <p className="font-mono text-[11px] text-[#525252] break-all">{session_id}</p>
          </div>
          <div className="flex items-center gap-3 font-mono text-[10px]">
            <div className="flex items-center gap-2">
              <span className="text-[#525252]">ANALYSE</span>
              <span data-testid="fsd-analysis-status" style={{ color: detail?.analysis_status === "READY" ? "#22C55E" : detail?.analysis_status === "FAILED" ? "#EF4444" : "#F59E0B" }}>
                {detail?.analysis_status || "…"}
              </span>
            </div>
            {isAdmin && (
              <button data-testid="fsd-delete" onClick={() => setDelOpen(true)}
                className="flex items-center gap-1.5 uppercase tracking-wider border border-[#EF4444]/40 text-[#EF4444] rounded-sm px-2.5 py-1 hover:bg-[#EF4444]/10 transition-colors">
                <Trash2 size={12} /> Supprimer
              </button>
            )}
          </div>
        </div>

        <ConfirmDialog open={delOpen} danger testid="fsd-delete-confirm"
          title="Supprimer la session"
          message="Suppression définitive : ULog, logs Agent, bundle, analyses dérivées et métadonnées Backend. Cette opération est irréversible."
          confirmLabel={delBusy ? "Suppression…" : "Supprimer définitivement"}
          onConfirm={doDelete} onCancel={() => setDelOpen(false)} />

        <UlogIntegrityCard integrity={detail?.ulog_integrity} />

        {/* Download bar (M) */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="fsd-downloads">
          <DownloadBtn testid="dl-ulog" disabled={!ready.ulog} label="ULog"
            size={fileSize("raw/flight.ulg")} onClick={() => doDownload(`${base}/download/ulog`, `${session_id}.ulg`)} icon={FileText} />
          <DownloadBtn testid="dl-bundle" disabled={!ready.bundle} label="Bundle ZIP"
            onClick={() => doDownload(`${base}/download/bundle`, `${session_id}-bundle.zip`)} icon={FileArchive} />
          <DownloadBtn testid="dl-summary" disabled={!detail?.derived?.summary} label="flight_summary.json"
            size={fileSize("derived/flight_summary.json")} onClick={() => doDownload(`${base}/download/file?path=derived/flight_summary.json`, "flight_summary.json")} icon={Download} />
          <DownloadBtn testid="dl-timeline" disabled={!detail?.derived?.timeline} label="timeline.json"
            size={fileSize("derived/timeline.json")} onClick={() => doDownload(`${base}/download/file?path=derived/timeline.json`, "timeline.json")} icon={Download} />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/10 mb-4" data-testid="fsd-tabs">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`fsd-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 font-mono text-[11px] uppercase px-3 py-2 border-b-2 transition-colors ${tab === t.id ? "border-[#3B82F6] text-white" : "border-transparent text-[#525252] hover:text-[#A3A3A3]"}`}>
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>

        {/* Résumé */}
        {tab === "summary" && (
          <div data-testid="fsd-panel-summary">
            {!s && <div className="font-mono text-[11px] text-[#525252] py-6">Résumé non disponible (analyse {detail?.analysis_status || "…"}).</div>}
            {s && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <StatCard testid="sum-duration" label="Durée" value={s.duration_s != null ? `${s.duration_s} s` : "—"} />
                  <StatCard testid="sum-armed" label="Armé" value={s.arm?.executed ? "oui" : "non"} />
                  <StatCard testid="sum-takeoff" label="Alt. max" value={s.takeoff?.max_altitude_m != null ? `${s.takeoff.max_altitude_m} m` : "—"} />
                  <StatCard testid="sum-offboard" label="OFFBOARD" value={s.offboard?.entered ? `${s.offboard.duration_s ?? "?"} s` : "non"} />
                  <StatCard testid="sum-distance" label="Distance" value={s.position?.distance_m != null ? `${s.position.distance_m} m` : "—"} />
                  <StatCard testid="sum-speed" label="V. sol max" value={s.position?.max_ground_speed_mps != null ? `${s.position.max_ground_speed_mps} m/s` : "—"} />
                  <StatCard testid="sum-gps" label="Sat. min / HDOP max" value={`${s.gps?.min_satellites ?? "—"} / ${s.gps?.max_hdop ?? "—"}`} />
                  <StatCard testid="sum-battery" label="Batt. consommée" value={s.battery?.consumed_percent != null ? `${s.battery.consumed_percent} %` : "—"} />
                </div>
                {s.failsafes?.length > 0 && (
                  <div className="bg-[#1a0f0f] border border-[#EF4444]/30 rounded-sm px-3 py-2" data-testid="sum-failsafes">
                    <div className="font-mono text-[10px] uppercase text-[#EF4444] mb-1">Failsafes</div>
                    <div className="font-mono text-[11px] text-[#F87171]">{s.failsafes.join(", ")}</div>
                  </div>
                )}
                {s.data_quality?.length > 0 && (
                  <div className="bg-[#111] border border-white/10 rounded-sm px-3 py-2" data-testid="sum-data-quality">
                    <div className="font-mono text-[10px] uppercase text-[#525252] mb-1">Qualité des données</div>
                    <div className="font-mono text-[11px] text-[#A3A3A3]">{s.data_quality.join(" · ")}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Timeline */}
        {tab === "timeline" && (
          <div data-testid="fsd-panel-timeline">
            {timeline === null && <div className="font-mono text-[11px] text-[#525252] py-6">Chargement…</div>}
            {timeline?.length === 0 && <div className="font-mono text-[11px] text-[#525252] py-6">Aucun événement.</div>}
            {timeline && timeline.length > 0 && (
              <div className="relative pl-4 border-l border-white/10 space-y-2">
                {timeline.map((e, i) => (
                  <div key={i} className="relative" data-testid={`tl-event-${i}`}>
                    <span className="absolute -left-[21px] top-1 w-2 h-2 rounded-full" style={{ background: SEV_COLOR[e.severity] || "#737373" }} />
                    <div className="flex items-center gap-2 font-mono text-[11px]">
                      <span className="text-[#525252] w-14">{e.relative_time_s != null ? `${e.relative_time_s.toFixed(1)}s` : "—"}</span>
                      <span className="text-[#3B82F6] w-16">{e.source}</span>
                      <span className="text-[#A3A3A3] w-20">{e.category}</span>
                      <span style={{ color: SEV_COLOR[e.severity] || "#fff" }}>{e.event}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Courbes */}
        {tab === "charts" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="fsd-panel-charts">
            {detail?.derived?.parquet
              ? CHARTS.map((c) => <SeriesChart key={c.metric} droneId={drone_id} sessionId={session_id} {...c} />)
              : <div className="font-mono text-[11px] text-[#525252] py-6">Courbes indisponibles (parquet non généré).</div>}
          </div>
        )}

        {/* Événements */}
        {tab === "events" && (
          <div data-testid="fsd-panel-events" className="border border-white/10 rounded-sm overflow-hidden">
            <table className="w-full text-left">
              <thead><tr className="bg-[#111] font-mono text-[9px] uppercase tracking-[0.15em] text-[#525252]">
                <th className="px-3 py-2">t (s)</th><th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Catégorie</th><th className="px-3 py-2">Événement</th>
                <th className="px-3 py-2">Sévérité</th>
              </tr></thead>
              <tbody>
                {(timeline || []).map((e, i) => (
                  <tr key={i} className="border-t border-white/5 font-mono text-[11px]">
                    <td className="px-3 py-1.5 text-[#525252]">{e.relative_time_s != null ? e.relative_time_s.toFixed(2) : "—"}</td>
                    <td className="px-3 py-1.5 text-[#3B82F6]">{e.source}</td>
                    <td className="px-3 py-1.5 text-[#A3A3A3]">{e.category}</td>
                    <td className="px-3 py-1.5">{e.event}</td>
                    <td className="px-3 py-1.5" style={{ color: SEV_COLOR[e.severity] }}>{e.severity}</td>
                  </tr>
                ))}
                {timeline?.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center font-mono text-[11px] text-[#525252]">Aucun événement.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Fichiers */}
        {tab === "files" && (
          <div data-testid="fsd-panel-files" className="border border-white/10 rounded-sm overflow-hidden">
            <table className="w-full text-left">
              <thead><tr className="bg-[#111] font-mono text-[9px] uppercase tracking-[0.15em] text-[#525252]">
                <th className="px-3 py-2">Fichier</th><th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Taille</th><th className="px-3 py-2"></th>
              </tr></thead>
              <tbody>
                {files === null && <tr><td colSpan={4} className="px-3 py-6 text-center font-mono text-[11px] text-[#525252]">Chargement…</td></tr>}
                {(files || []).map((f) => (
                  <tr key={f.path} className="border-t border-white/5 font-mono text-[11px]">
                    <td className="px-3 py-1.5 break-all">{f.path}</td>
                    <td className="px-3 py-1.5 text-[#A3A3A3]">{f.type}</td>
                    <td className="px-3 py-1.5 text-[#A3A3A3]">{fmtBytes(f.size)}</td>
                    <td className="px-3 py-1.5">
                      {ready.bundle && (
                        <button data-testid={`file-dl-${f.path}`} onClick={() => doDownload(`${base}/download/file?path=${encodeURIComponent(f.path)}`, f.path.split("/").pop())}
                          className="text-[#3B82F6] hover:underline">télécharger</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Analyse IA */}
        {tab === "ai" && (
          <div data-testid="fsd-panel-ai" className="space-y-4">
            <div className="flex gap-2">
              <button data-testid="ai-quick-btn" onClick={() => runAi("quick")} disabled={aiBusy !== ""}
                className="flex items-center gap-1.5 font-mono text-[11px] uppercase border border-[#3B82F6]/40 text-[#3B82F6] rounded-sm px-3 py-1.5 hover:bg-[#3B82F6]/10 disabled:opacity-40">
                {aiBusy === "quick" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Analyse rapide
              </button>
              <button data-testid="ai-deep-btn" onClick={() => runAi("deep")} disabled={aiBusy !== ""}
                className="flex items-center gap-1.5 font-mono text-[11px] uppercase border border-[#A78BFA]/40 text-[#A78BFA] rounded-sm px-3 py-1.5 hover:bg-[#A78BFA]/10 disabled:opacity-40">
                {aiBusy === "deep" ? <Loader2 size={13} className="animate-spin" /> : <Brain size={13} />} Analyse profonde
              </button>
            </div>
            <p className="font-mono text-[10px] text-[#525252]">Analyse READ-ONLY, historique uniquement. L'IA ne peut émettre aucune commande.</p>
            {["quick", "deep"].map((m) => ai[m] && (
              <div key={m} className="bg-[#111] border border-white/10 rounded-sm p-3" data-testid={`ai-result-${m}`}>
                <div className="font-mono text-[10px] uppercase text-[#525252] mb-2">{m} {ai[m].cached ? "(cache)" : ""} · {ai[m].model}</div>
                <pre className="font-mono text-[11px] text-[#D4D4D4] whitespace-pre-wrap leading-relaxed">{ai[m].result}</pre>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DownloadBtn({ label, size, onClick, disabled, icon: Icon, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className="flex items-center gap-1.5 font-mono text-[11px] border border-white/15 rounded-sm px-3 py-1.5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
      <Icon size={13} /> {label}
      {size != null && <span className="text-[#525252]">· {fmtBytes(size)}</span>}
    </button>
  );
}
